import PendingStockOperationNotice from "../../components/stock/PendingStockOperationNotice";
// src/pages/Stock/MaterialExceptionsPage.jsx
//
// Material Consumption Review page.
// =============================================================================
//
// PURPOSE
// -------
// This page is the manager/backoffice work queue for service material
// consumption generated by ManagePage visit checks.
//
// WHY THIS PAGE EXISTS
// --------------------
// The salon employee must not be blocked while checking a visit. If stock or
// master-data is incomplete, the backend creates an exception row and the visit
// can still be checked. This page lets the manager resolve those exceptions
// later without asking the employee to repeat the visit workflow.
//
// It also shows already posted material rows, because a practical salon case is:
// "standard material was available and automatically consumed, but the employee
// actually used a different product." In that case manager can use Replace
// product, which reverses the standard product movement and posts the real
// product movement.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import StockPostingContextCard from "../../components/stock/StockPostingContextCard";
import StockMovementAuditLoader from "../../components/stock/StockMovementAuditLoader";
import { stockPostingErrorMessage } from "../../utils/stockPostingPresentation";
import ProductPicker from "../../components/catalog/pickers/ProductPicker";
import {
  listMaterialConsumptionReview,
  reprocessMaterialConsumption,
  skipMaterialConsumption,
  useAnotherLocationForMaterial,
  useSubstituteProductForMaterial,
  replacePostedMaterialProduct,
} from "../../services/serviceConsumptionService";
import { fetchStockLocations, searchStockProducts } from "../../services/stockService";
import { exceptionReasonLabel, statusLabel as translatedStatusLabel } from "../../utils/i18nLabels";

import "./MaterialExceptionsPage.css";

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function productName(product) {
  return product?.name_public || product?.name || "—";
}

function productSku(t, product) {
  const parts = [
    product?.sku ? `SKU: ${product.sku}` : null,
    product?.barcode ? `${t("serviceConsumption:materialReview.barcodeLabel")}: ${product.barcode}` : null,
  ].filter(Boolean);

  return parts.join(" · ") || t("serviceConsumption:materialReview.noSkuBarcode");
}

function formatVisit(t, row) {
  const visit = row?.visit;
  const visitLabel = t("serviceConsumption:materialReview.visit");
  if (!visit) return `${visitLabel} ${row?.visit_id || "—"}`;
  const client = visit.client ? ` · ${visit.client}` : "";
  const service = visit.service ? ` · ${visit.service}` : "";
  return `${visitLabel} ${visit.visit_id}${client}${service}`;
}

function resolutionTypeLabel(t, value) {
  if (!value) return "";
  return t(`serviceConsumption:resolutionTypes.${value}`, { defaultValue: value });
}

function isException(row) {
  return row?.status === "exception";
}

function isPosted(row) {
  return row?.status === "posted";
}

const emptyAction = {
  row: null,
  type: "",
  comment: "",
  locationId: "",
  product: null,
  businessDate: "",
};

const MaterialExceptionsPage = () => {
  const { t } = useTranslation(["common", "navigation", "stock", "serviceConsumption"]);

  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [action, setAction] = useState(emptyAction);

  const [lastResult, setLastResult] = useState(null);
  const [auditMovementId, setAuditMovementId] = useState(null);
  const selectedRow = action.row;

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += 1;
        if (row.status === "exception") acc.exceptions += 1;
        if (row.status === "posted") acc.posted += 1;
        return acc;
      },
      { total: 0, exceptions: 0, posted: 0 }
    );
  }, [rows]);

  async function loadRows(preserveOutcome = false) {
    setLoading(true);
    if (!preserveOutcome) { setError(""); setInfo(""); }

    try {
      const [reviewRows, stockLocations] = await Promise.all([
        listMaterialConsumptionReview({ limit: 250 }),
        fetchStockLocations(),
      ]);
      setRows(Array.isArray(reviewRows) ? reviewRows : []);
      setLocations(Array.isArray(stockLocations) ? stockLocations : []);
    } catch (err) {
      if (preserveOutcome) setError(prev => [prev, t("stock:postingSafety.refreshFailed")].filter(Boolean).join(" "));
      else setError(err?.message || t("serviceConsumption:materialReview.loadError"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  function beginAction(row, type) {
    setError("");
    setInfo("");
    setAction({
      row,
      type,
      comment: "",
      locationId: row?.stock_location_id ? String(row.stock_location_id) : "",
      product: null,
      businessDate: row.stock_business_date || row.effective_stock_business_date || "",
    });
  }

  function cancelAction() {
    setAction(emptyAction);
  }

  async function submitAction() {
    if (!selectedRow || !action.type || !canSubmitAction(action)) return;
    setSaving(true); setError(""); setInfo("");
    let result;
    try {
      const payload = { business_date: action.businessDate || undefined, comment: action.comment,
        location_id: action.locationId || selectedRow.stock_location_id, product_id: action.product?.id };
      if (action.type === "reprocess") result = await reprocessMaterialConsumption(selectedRow.id, payload);
      else if (action.type === "skip") result = await skipMaterialConsumption(selectedRow.id, payload);
      else if (action.type === "location") result = await useAnotherLocationForMaterial(selectedRow.id, payload);
      else if (action.type === "substitute") result = await useSubstituteProductForMaterial(selectedRow.id, payload);
      else if (action.type === "replace") result = await replacePostedMaterialProduct(selectedRow.id, payload);
      if (result?.status === "exception") {
        setError(stockPostingErrorMessage(result.stock_posting_error_json || { message: t("stock:integration.materialStillUnposted") }, t));
      } else {
        setLastResult(result); setInfo(t("stock:integration.sourceActionCompleted")); setAction(emptyAction);
      }
      // Failure to reload is not a failed or duplicate stock transaction.
      await loadRows(true);
    } catch (err) { setError(stockPostingErrorMessage(err, t)); }
    finally { setSaving(false); }
  }

  function renderActionPanel() {
    if (!selectedRow || !action.type) return null;

    const needsProduct = action.type === "substitute" || action.type === "replace";
    const needsLocation = action.type === "location" || action.type === "substitute" || action.type === "replace";
    const needsComment = action.type === "skip" || action.type === "substitute" || action.type === "replace" || action.type === "location";

    return (
      <section className="mat-ex-actionPanel">
        <div className="mat-ex-actionPanel__head">
          <div>
            <strong>{actionTitle(t, action.type)}</strong>
            <p>{actionDescription(t, action.type)}</p>
          </div>
          <button type="button" className="mat-ex-btn mat-ex-btn--soft" onClick={cancelAction} disabled={saving}>
            {t("common:actions.cancel")}
          </button>
        </div>

        <div className="mat-ex-actionPanel__context">
          <span>{t("common:common.row")} #{selectedRow.id}</span>
          <span>{formatVisit(t, selectedRow)}</span>
          <span>{productName(selectedRow.actualProduct || selectedRow.plannedProduct)}</span>
        </div>

        {action.type !== "skip" && <label className="mat-ex-field">
          {t("stock:integration.stockBusinessDate")}
          <input type="date" value={action.businessDate} disabled={saving || !selectedRow.stock_date_requires_confirmation}
            required onChange={(event) => setAction(prev => ({ ...prev, businessDate: event.target.value }))} />
          <small>{t(selectedRow.stock_date_requires_confirmation ? "stock:integration.legacyDateConfirmation" : "stock:integration.materialDateFrozen")}</small>
          {action.type === "replace" && <small>{t("stock:integration.replacementDateHint")}</small>}
        </label>}
        {needsProduct ? (
          <ProductPicker
            label={action.type === "replace" ? t("serviceConsumption:materialReview.actualProduct") : t("serviceConsumption:actions.useSubstituteProduct")}
            browseLabel={t("common:actions.browse")}
            placeholder={t("stock:overview.filterPlaceholder")}
            selectedProduct={action.product}
            onSelect={(product) => setAction((prev) => ({ ...prev, product }))}
            searchProducts={searchStockProducts}
            searchParams={{ location_id: action.locationId || selectedRow.stock_location_id || undefined }}
            context="stock"
            helperText={t("serviceConsumption:actionDescriptions.substitute")}
            disabled={saving}
          />
        ) : null}

        {needsLocation ? (
          <label className="mat-ex-field">
            {t("serviceConsumption:materialReview.stockLocation")}
            <select
              value={action.locationId}
              onChange={(event) => setAction((prev) => ({ ...prev, locationId: event.target.value }))}
              disabled={saving}
            >
              <option value="">{t("serviceConsumption:materialReview.stockLocation")}…</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {needsComment ? (
          <label className="mat-ex-field">
            {t("common:common.optionalComment")}{action.type === "skip" ? " *" : ""}
            <textarea
              value={action.comment}
              onChange={(event) => setAction((prev) => ({ ...prev, comment: event.target.value }))}
              placeholder={t("common:common.optionalComment")}
              disabled={saving}
              rows={3}
            />
          </label>
        ) : null}

        <div className="mat-ex-actionPanel__buttons">
          <button
            type="button"
            className="mat-ex-btn"
            disabled={saving || !canSubmitAction(action)}
            onClick={submitAction}
          >
            {saving ? t("common:common.loading") : t("common:actions.confirm")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="mat-ex-page">
      <PendingStockOperationNotice scope="material" onResolved={async (row) => { setLastResult(row); setAction(emptyAction); await loadRows(true); }} />
      <header className="mat-ex-header">
        <div>
          <span className="mat-ex-kicker">{t("navigation:stock")} / {t("serviceConsumption:materialReview.title")}</span>
          <h1>{t("serviceConsumption:materialReview.title")}</h1>
          <p>{t("serviceConsumption:materialReview.subtitle")}</p>
        </div>

        <div className="mat-ex-actions">
          <Link className="mat-ex-btn mat-ex-btn--soft" to="/stock">
            {t("common:actions.back")}
          </Link>
          <button className="mat-ex-btn" type="button" onClick={loadRows} disabled={loading || saving}>
            {t("common:actions.refresh")}
          </button>
        </div>
      </header>

      {error ? <div className="mat-ex-alert mat-ex-alert--error">{error}</div> : null}
      {info ? <div className="mat-ex-alert mat-ex-alert--info">{info}</div> : null}

      <StockMovementAuditLoader movementId={auditMovementId} onClose={() => setAuditMovementId(null)} />
      {lastResult && <StockPostingContextCard snapshot={lastResult.stock_posting_result_json} movementId={lastResult.stock_movement_id} onOpenMovement={setAuditMovementId} />}
      <p className="stock-read-context">{t("stock:integration.currentAvailabilityHint")}</p>
      {renderActionPanel()}

      <section className="mat-ex-card">
        <div className="mat-ex-card__head">
          <strong>{t("serviceConsumption:materialReview.title")}</strong>
          <span>
            {counts.total} {t("serviceConsumption:materialReview.summaryTotal")} · {counts.exceptions} {t("serviceConsumption:materialReview.summaryExceptions")} · {counts.posted} {t("serviceConsumption:materialReview.summaryPosted")}
          </span>
        </div>

        <div className="mat-ex-tableWrap">
          <table className="mat-ex-table">
            <thead>
              <tr>
                <th>{t("serviceConsumption:materialReview.status")}</th>
                <th>{t("serviceConsumption:materialReview.visit")}</th>
                <th>{t("serviceConsumption:materialReview.plannedProduct")} / {t("serviceConsumption:materialReview.actualProduct")}</th>
                <th>{t("stock:actions.service_consumption")}</th>
                <th>{t("serviceConsumption:materialReview.requiredStockQty")}</th>
                <th>{t("serviceConsumption:materialReview.availableStock")}</th>
                <th>{t("serviceConsumption:materialReview.stockLocation")}</th>
                <th>{t("serviceConsumption:materialReview.reason")} / {t("common:common.comment")}</th>
                <th>{t("serviceConsumption:materialReview.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9">{t("common:common.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="9">{t("serviceConsumption:materialReview.noRows")}</td></tr>
              ) : (
                rows.map((row) => {
                  const product = row.actualProduct || row.plannedProduct;
                  const planned = row.plannedProduct;
                  const actual = row.actualProduct;
                  const substituted = planned && actual && String(planned.id) !== String(actual.id);

                  return (
                    <tr key={row.id} className={row.status === "exception" ? "mat-ex-row--exception" : ""}>
                      <td>
                        <span className={`mat-ex-status mat-ex-status--${row.status}`}>
                          {translatedStatusLabel(t, row.status === "exception" ? "needs_review" : row.status)}
                        </span>
                      </td>
                      <td>{formatVisit(t, row)}<small>{t("stock:integration.stockBusinessDate")}: {row.stock_business_date || row.effective_stock_business_date || t("stock:integration.dateNotRecorded")}</small></td>
                      <td>
                        <strong>{productName(product)}</strong>
                        <small>{productSku(t, product)}</small>
                        {substituted ? (
                          <small>{t("serviceConsumption:materialReview.plannedProduct")}: {productName(planned)}</small>
                        ) : null}
                        <small>{row.service?.name_public || row.service?.service_code || "—"}</small>
                      </td>
                      <td>
                        {formatDecimal(row.actual_quantity ?? row.planned_quantity)} {row.actual_unit_code || row.planned_unit_code || ""}
                      </td>
                      <td>
                        {formatDecimal(row.stock_quantity, 6)} {row.stock_unit_code || ""}
                      </td>
                      <td>
                        {formatDecimal(row.available_stock_quantity, 6)} {row.stock_unit_code || product?.stock_unit_code || ""}
                      </td>
                      <td>{row.stockLocation ? `${row.stockLocation.name} (${row.stockLocation.code})` : "—"}</td>
                      <td>
                        {row.stock_posting_error_json && <small role="status">{stockPostingErrorMessage(row.stock_posting_error_json, t)}</small>}
                        {row.exception_reason ? <span className="mat-ex-reason">{exceptionReasonLabel(t, row.exception_reason)}</span> : null}
                        {row.resolution_type ? <small>{t("serviceConsumption:materialReview.status")}: {resolutionTypeLabel(t, row.resolution_type)}</small> : null}
                        {row.comment ? <small>{row.comment}</small> : null}
                      </td>
                      <td>
                        <StockPostingContextCard snapshot={row.stock_posting_result_json} movementId={row.stock_movement_id} onOpenMovement={setAuditMovementId} />
                        <div className="mat-ex-rowActions">
                          {isException(row) ? (
                            <>
                              <button type="button" onClick={() => beginAction(row, "reprocess")} disabled={saving}>{t("serviceConsumption:actions.reprocess")}</button>
                              <button type="button" onClick={() => beginAction(row, "location")} disabled={saving}>{t("serviceConsumption:actions.useAnotherLocation")}</button>
                              <button type="button" onClick={() => beginAction(row, "substitute")} disabled={saving}>{t("serviceConsumption:actions.useSubstituteProduct")}</button>
                              <button type="button" onClick={() => beginAction(row, "skip")} disabled={saving}>{t("serviceConsumption:actions.skipWithComment")}</button>
                            </>
                          ) : null}

                          {isPosted(row) ? (
                            <button type="button" onClick={() => beginAction(row, "replace")} disabled={saving}>{t("serviceConsumption:actions.replaceProduct")}</button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

function actionTitle(t, type) {
  if (type === "reprocess") return t("serviceConsumption:actions.reprocess");
  if (type === "skip") return t("serviceConsumption:actions.skipWithComment");
  if (type === "location") return t("serviceConsumption:actions.useAnotherLocation");
  if (type === "substitute") return t("serviceConsumption:actions.useSubstituteProduct");
  if (type === "replace") return t("serviceConsumption:actions.replaceProduct");
  return t("serviceConsumption:materialReview.actions");
}

function actionDescription(t, type) {
  if (type === "reprocess") {
    return t("serviceConsumption:actionDescriptions.reprocess");
  }
  if (type === "skip") {
    return t("serviceConsumption:actionDescriptions.skip");
  }
  if (type === "location") {
    return t("serviceConsumption:actionDescriptions.location");
  }
  if (type === "substitute") {
    return t("serviceConsumption:actionDescriptions.substitute");
  }
  if (type === "replace") {
    return t("serviceConsumption:actionDescriptions.replace");
  }
  return "";
}

function canSubmitAction(action) {
  if (!action?.row || !action.type) return false;
  if (action.type !== "skip" && action.row.stock_date_requires_confirmation && !/^\d{4}-\d{2}-\d{2}$/.test(action.businessDate || "")) return false;
  if (action.type === "skip") return !!String(action.comment || "").trim();
  if (action.type === "location") return !!action.locationId;
  if (action.type === "substitute" || action.type === "replace") return !!action.product?.id && !!action.locationId;
  return true;
}

export default MaterialExceptionsPage;
